import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Tier = 'free' | 'basic' | 'standard' | 'pro' | 'enterprise';
export type BillingPeriod = 'monthly' | 'yearly';

export interface TierLimits {
  maxBoards: number;         // -1 = unlimited
  maxSeats: number;          // -1 = unlimited
  maxAutomations: number;    // -1 = unlimited
  aiRequestsPerDay: number;  // -1 = unlimited
  storageGB: number;         // -1 = unlimited
  exports: boolean;
  dashboards: boolean;
  integrations: boolean;
  privateBoards: boolean;
  chartView: boolean;
  formulaColumn: boolean;
  timeTracking: boolean;
  sso: boolean;
  auditLog: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
}

export const TIER_META: Record<Tier, { name: string; blurb: string; price: { monthly: number; yearly: number } | 'custom' }> = {
  free:       { name: 'Free',       blurb: 'To dip your toes in.',      price: { monthly: 0,  yearly: 0 } },
  basic:      { name: 'Basic',      blurb: 'For the whole team.',       price: { monthly: 8,  yearly: 6.4 } },
  standard:   { name: 'Standard',   blurb: 'Our most popular plan.',    price: { monthly: 10, yearly: 8 } },
  pro:        { name: 'Pro',        blurb: 'Power features unlocked.',  price: { monthly: 19, yearly: 15.2 } },
  enterprise: { name: 'Enterprise', blurb: 'Scale, security, success.', price: 'custom' },
};

export const TIER_LIMITS: Record<Tier, TierLimits> = {
  free: {
    maxBoards: 3, maxSeats: 2, maxAutomations: 0, aiRequestsPerDay: 10, storageGB: 0.5,
    exports: false, dashboards: false, integrations: false, privateBoards: false,
    chartView: false, formulaColumn: false, timeTracking: false, sso: false,
    auditLog: false, customBranding: false, prioritySupport: false,
  },
  basic: {
    maxBoards: -1, maxSeats: -1, maxAutomations: 0, aiRequestsPerDay: 50, storageGB: 1,
    exports: true, dashboards: false, integrations: false, privateBoards: false,
    chartView: false, formulaColumn: false, timeTracking: false, sso: false,
    auditLog: false, customBranding: false, prioritySupport: false,
  },
  standard: {
    maxBoards: -1, maxSeats: -1, maxAutomations: 250, aiRequestsPerDay: 200, storageGB: 20,
    exports: true, dashboards: true, integrations: true, privateBoards: false,
    chartView: true, formulaColumn: false, timeTracking: false, sso: false,
    auditLog: false, customBranding: false, prioritySupport: false,
  },
  pro: {
    maxBoards: -1, maxSeats: -1, maxAutomations: 25_000, aiRequestsPerDay: -1, storageGB: 100,
    exports: true, dashboards: true, integrations: true, privateBoards: true,
    chartView: true, formulaColumn: true, timeTracking: true, sso: false,
    auditLog: false, customBranding: false, prioritySupport: true,
  },
  enterprise: {
    maxBoards: -1, maxSeats: -1, maxAutomations: -1, aiRequestsPerDay: -1, storageGB: -1,
    exports: true, dashboards: true, integrations: true, privateBoards: true,
    chartView: true, formulaColumn: true, timeTracking: true, sso: true,
    auditLog: true, customBranding: true, prioritySupport: true,
  },
};

export interface Invoice {
  id: string;
  amount: number;
  currency: 'USD';
  status: 'paid' | 'pending' | 'failed';
  periodStart: string;
  periodEnd: string;
  paidAt?: string;
  downloadUrl?: string; // mocked
}

interface BillingState {
  tier: Tier;
  period: BillingPeriod;
  seats: number;                  // how many seats purchased
  subscriptionStartedAt?: string;
  nextBillDate?: string;
  cardLast4?: string;
  cardBrand?: string;
  upgradeModalFeature: string | null;
  invoices: Invoice[];
  aiRequestsToday: number;
  aiRequestsResetAt: string;

  setTier: (tier: Tier) => void;
  setPeriod: (period: BillingPeriod) => void;
  setSeats: (seats: number) => void;
  openUpgrade: (feature?: string) => void;
  closeUpgrade: () => void;
  attachCard: (brand: string, last4: string) => void;
  addInvoice: (inv: Omit<Invoice, 'id'>) => void;
  recordAiRequest: () => boolean;
  reset: () => void;
}

function mockInvoices(tier: Tier, period: BillingPeriod, seats: number): Invoice[] {
  const meta = TIER_META[tier];
  if (meta.price === 'custom' || meta.price.monthly === 0) return [];
  const priceEach = period === 'yearly' ? meta.price.yearly : meta.price.monthly;
  const monthlyTotal = priceEach * seats;
  const out: Invoice[] = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    out.push({
      id: `inv_${d.getTime()}`,
      amount: monthlyTotal,
      currency: 'USD',
      status: 'paid',
      periodStart: start.toISOString().slice(0, 10),
      periodEnd: end.toISOString().slice(0, 10),
      paidAt: start.toISOString(),
    });
  }
  return out;
}

const DEFAULT_TIER: Tier = 'free';

export const useBillingStore = create<BillingState>()(
  persist(
    (set, get) => ({
      tier: DEFAULT_TIER,
      period: 'monthly',
      seats: 1,
      upgradeModalFeature: null,
      invoices: mockInvoices(DEFAULT_TIER, 'monthly', 1),
      aiRequestsToday: 0,
      aiRequestsResetAt: new Date().toISOString().slice(0, 10),

      setTier: (tier) => {
        const now = new Date();
        const next = new Date(now);
        next.setMonth(next.getMonth() + (get().period === 'yearly' ? 12 : 1));
        set({
          tier,
          subscriptionStartedAt: now.toISOString(),
          nextBillDate: next.toISOString(),
          invoices: mockInvoices(tier, get().period, get().seats),
          upgradeModalFeature: null,
        });
      },
      setPeriod: (period) => {
        set({ period, invoices: mockInvoices(get().tier, period, get().seats) });
      },
      setSeats: (seats) => {
        set({ seats: Math.max(1, seats), invoices: mockInvoices(get().tier, get().period, Math.max(1, seats)) });
      },
      openUpgrade: (feature) => set({ upgradeModalFeature: feature ?? 'this plan' }),
      closeUpgrade: () => set({ upgradeModalFeature: null }),
      attachCard: (brand, last4) => set({ cardBrand: brand, cardLast4: last4 }),
      addInvoice: (inv) => set((s) => ({ invoices: [{ ...inv, id: `inv_${Math.random().toString(36).slice(2, 10)}` }, ...s.invoices] })),

      recordAiRequest: () => {
        const today = new Date().toISOString().slice(0, 10);
        const limits = TIER_LIMITS[get().tier];
        // Reset daily counter
        if (get().aiRequestsResetAt !== today) {
          set({ aiRequestsToday: 0, aiRequestsResetAt: today });
        }
        if (limits.aiRequestsPerDay !== -1 && get().aiRequestsToday >= limits.aiRequestsPerDay) {
          return false; // quota exceeded
        }
        set({ aiRequestsToday: get().aiRequestsToday + 1 });
        return true;
      },

      reset: () => set({
        tier: DEFAULT_TIER, period: 'monthly', seats: 1,
        invoices: mockInvoices(DEFAULT_TIER, 'monthly', 1),
        aiRequestsToday: 0, aiRequestsResetAt: new Date().toISOString().slice(0, 10),
        upgradeModalFeature: null,
      }),
    }),
    { name: 'myday-billing', version: 1 },
  ),
);

// ===== Helpers =====
export function tierHasFeature(tier: Tier, feature: keyof TierLimits): boolean {
  const v = TIER_LIMITS[tier][feature];
  if (typeof v === 'boolean') return v;
  return v === -1 || (typeof v === 'number' && v > 0);
}

export function formatLimit(value: number) {
  if (value === -1) return 'Unlimited';
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return String(value);
}
